import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import EmployerLayout from '../../layouts/EmployerLayout';
import Button from '../../components/common/Button';
import Badge from '../../components/common/Badge';
import toast from 'react-hot-toast';

const STATUSES = ['', 'draft', 'pending_approval', 'active', 'paused', 'closed'];

export default function EmployerRequirements() {
  const [reqs, setReqs] = useState([]);
  const [pagination, setPagination] = useState({});
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState('');
  const [submitting, setSubmitting] = useState(null);

  function load() {
    setLoading(true);
    const params = new URLSearchParams({ page, limit: 10, ...(status && { status }) });
    api.get(`/employer/requirements?${params}`)
      .then((r) => { setReqs(r.data.data || []); setPagination(r.data.pagination || {}); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }

  useEffect(load, [page, status]);

  async function submitForApproval(id) {
    setSubmitting(id);
    try {
      await api.post(`/requirements/${id}/submit`);
      toast.success('Submitted for approval');
      load();
    } catch {}
    finally { setSubmitting(null); }
  }

  return (
    <EmployerLayout>
      <div className="space-y-5">
        <div className="flex items-center justify-between">
          <h1 className="text-xl font-bold text-gray-900">My Requirements</h1>
          <Link to="/employer/requirements/new">
            <Button size="sm">+ Post New</Button>
          </Link>
        </div>

        {/* Status Filter */}
        <div className="flex gap-2 overflow-x-auto pb-1">
          {[['', 'All'], ['draft', 'Draft'], ['pending_approval', 'Pending'], ['active', 'Active'], ['paused', 'Paused'], ['closed', 'Closed']].map(([val, label]) => (
            <button key={val} type="button"
              onClick={() => { setStatus(val); setPage(1); }}
              className={`px-3 py-1.5 rounded-full text-xs border whitespace-nowrap transition-colors ${
                status === val ? 'bg-india text-white border-india' : 'border-gray-300 text-gray-600 hover:bg-gray-50'
              }`}>
              {label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-16">
            <div className="animate-spin h-8 w-8 border-4 border-india border-t-transparent rounded-full" />
          </div>
        ) : reqs.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-xl border border-gray-200">
            <span className="text-4xl">📋</span>
            <p className="text-gray-500 mt-3">No requirements found</p>
            <Link to="/employer/requirements/new">
              <Button size="sm" className="mt-4">Post Your First Requirement</Button>
            </Link>
          </div>
        ) : (
          <div className="space-y-3">
            {reqs.map((r) => (
              <div key={r.id} className="bg-white rounded-xl border border-gray-200 p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-gray-900 text-sm">{r.title}</h3>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {r.category_name} {r.district && `· ${r.district}`}
                    </p>
                    <div className="flex flex-wrap gap-3 mt-1.5 text-xs text-gray-500">
                      {r.number_of_workers > 1 && <span>👥 {r.number_of_workers} workers needed</span>}
                      {r.wage_min && <span>💰 ₹{r.wage_min}{r.wage_max ? `–${r.wage_max}` : ''}/{r.wage_type}</span>}
                      <span>📥 {r.applications_count || 0} applications</span>
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
                    <Badge label={r.status} variant={r.status} />
                    <Badge label={r.urgency} variant={r.urgency} />
                  </div>
                </div>
                <div className="flex gap-2 mt-3">
                  <Link to={`/employer/requirements/${r.id}`}>
                    <Button size="sm" variant="secondary">View</Button>
                  </Link>
                  {r.status === 'draft' && (
                    <Button size="sm" loading={submitting === r.id}
                      onClick={() => submitForApproval(r.id)}>
                      Submit for Approval
                    </Button>
                  )}
                  {r.status === 'active' && (
                    <Link to={`/employer/requirements/${r.id}/applicants`}>
                      <Button size="sm">View Applicants</Button>
                    </Link>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {pagination.totalPages > 1 && (
          <div className="flex justify-center gap-3">
            {pagination.hasPrev && (
              <Button size="sm" variant="secondary" onClick={() => setPage((p) => p - 1)}>← Prev</Button>
            )}
            <span className="text-sm text-gray-500 py-1.5">Page {pagination.page} of {pagination.totalPages}</span>
            {pagination.hasNext && (
              <Button size="sm" variant="secondary" onClick={() => setPage((p) => p + 1)}>Next →</Button>
            )}
          </div>
        )}
      </div>
    </EmployerLayout>
  );
}
